import React, { useState, type FormEvent } from 'react';
import { NumberInput, RadioButtonGroupInput } from 'react-admin';
import { getSourceAndLabel, getClassificationFromSource } from '../PositionRateSettingsForm.utils';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import { useRateCalculations } from '../hooks/useRateCalculations';
import { useFormContext } from 'react-hook-form';
import { getInheritedValue } from 'utils/rateSettings/SharedRateSettingsForm.utils';
import { PARENT_SETTINGS_FIELD } from '../constants';
import { W2BurdensTooltip } from 'resources/rate_settings/shared/W2BurdensTooltip';

type Props = {
  source: string;
  inheritedSettings: any;
};

const choices = [
  { id: 'lock_charge_rate', name: 'Lock Charge Rate' },
  { id: 'lock_professional_rate', name: 'Lock Professional Rate' },
];

export const MarginPercentInput = ({ source, inheritedSettings }: Props) => {
  const [isLockOptionVisible, setIsLockOptionVisible] = useState(false);
  const { watchedFields, handlers } = useRateCalculations({ source, inheritedSettings });
  const { getValues } = useFormContext();
  const parentSettings = getValues(PARENT_SETTINGS_FIELD);

  const { source: inputSource, label, field } = getSourceAndLabel(source);
  const { tab } = getClassificationFromSource(source);
  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({ settings: inheritedSettings });
  const inheritedSettingsHash = rateSettingsDisplayHelper.getSettingsForClassification(tab);
  const inheritedW2Burdens = rateSettingsDisplayHelper.getW2Burdens();

  const parentSettingsHash = new RateSettingsDisplayHelper({ settings: parentSettings });
  const parentClassificationHash = parentSettingsHash.getSettingsForClassification(tab);
  const isW2Local = field === 'total_margin_percent_w2';

  function handleLockOptionChange(event: FormEvent<HTMLDivElement>) {
    const value = (event.target as HTMLInputElement).value as
      | 'lock_professional_rate'
      | 'lock_charge_rate';
    handlers.handleLockTypeChange(value);
  }

  return (
    <>
      <W2BurdensTooltip w2Burdens={inheritedW2Burdens} enabled={isW2Local && !!inheritedW2Burdens}>
        <NumberInput
          source={inputSource}
          label={label}
          helperText={String(
            getInheritedValue(inheritedSettingsHash, field, parentClassificationHash) || '',
          )}
          onFocus={() => setIsLockOptionVisible(true)}
          onChange={(event) => {
            const newValue = parseFloat(event.target.value);
            if (!isNaN(newValue)) {
              handlers.handleMarginChange(newValue);
            }
          }}
          disabled={isW2Local}
        />
      </W2BurdensTooltip>
      {isLockOptionVisible && (
        <RadioButtonGroupInput
          source={`${source}.lockedRateType`}
          choices={choices}
          value={watchedFields.lockedRateType}
          defaultValue="lock_charge_rate"
          label="Calculation Base"
          helperText="Select which rate will be fixed. The other will be automatically calculated based on the entered margin."
          onChange={handleLockOptionChange}
        />
      )}
    </>
  );
};
